import { action, computed, observable } from 'mobx';
import { timestampMs } from '@bufbuild/protobuf/wkt';
import { Code, ConnectError } from '@connectrpc/connect';
import { Model } from 'core/model/Model';
import { ALARM_SUPPRESSED_FILTER, POLICY_APPLICATIONS, SEVERITY_MAP } from '@kentik/ui-shared/alerting/constants';
import $auth from 'app/stores/$auth';
import $alerting from 'app/stores/alerting/$alerting';
import AlertCollection from 'app/stores/alerting/AlertCollection';
import type AlertModel from 'app/stores/alerting/AlertModel';
import {
  CorrelationAckState,
  CorrelationClosedReason,
  CorrelationConfidenceLevel,
  CorrelationStatus,
  type Correlation as CorrelationProto
} from '@kentik/schema/gen/ts/kentik/alerting/correlation/v202609/correlation_pb';
import { Severity } from '@kentik/schema/gen/ts/kentik/alerting/types/v202303/types_pb';
import {
  CorrelationCloseReason,
  type ContextInput
} from '@kentik/schema/gen/ts/kentik/alerting/context/v202602/input_pb';
import {
  ackCorrelation,
  getCorrelation,
  removeAlertsFromCorrelation,
  unAckCorrelation,
  updateCorrelation
} from './correlationsApi';
import { CONFIDENCE_BAND_BY_LEVEL } from './constants';
import type {
  ConfidenceBand,
  CorrelationAckKey,
  CorrelationActivityEntry,
  CorrelationEntity,
  CorrelationMember,
  CorrelationPolicy,
  CorrelationStatusKey,
  AlertBaseline,
  DimensionKind,
  TypedAlertDimension
} from './types';

/** `alerts` is replaced by the slimmer `members`; the alerts themselves load into `alerts`. */
export type CorrelationAttributes = Omit<CorrelationProto, '$typeName' | '$unknown' | 'alerts'> & {
  members: CorrelationMember[];
  activity: CorrelationActivityEntry[];
};

/** A `Get` response carries the correlation's lifecycle events alongside it. */
export type CorrelationInput = CorrelationProto & { events?: ContextInput[] };

export default class CorrelationModel extends Model<CorrelationAttributes, keyof CorrelationAttributes> {
  private memberAlerts?: AlertCollection;

  get defaults() {
    return {
      title: '',
      summary: '',
      severity: Severity.UNSPECIFIED,
      status: CorrelationStatus.UNSPECIFIED,
      ackState: CorrelationAckState.UNSPECIFIED,
      ackedByUserId: BigInt(0),
      updatedByUserId: BigInt(0),
      closedByUserId: BigInt(0),
      confidenceLevel: CorrelationConfidenceLevel.UNSPECIFIED,
      closedReason: CorrelationClosedReason.UNSPECIFIED,
      mergedIntoId: '',
      activeAlertCount: 0,
      totalAlertCount: 0,
      members: [],
      activity: []
    };
  }

  /** Full alert details for the members, loaded separately via `fetchAlerts`. */
  get alerts(): AlertCollection {
    if (!this.memberAlerts) {
      this.memberAlerts = new AlertCollection();
      // Synthetics and archived-policy alarms have no listed policy; the whole set loads, so sorting stays local.
      this.memberAlerts.requirePolicy = false;
      this.memberAlerts.serverSortableFields = [];
      // Not shown on the correlation pages.
      this.memberAlerts.fetchMitigations = false;
    }
    return this.memberAlerts;
  }

  /**
   * The loaded member alerts, kept apart from `alerts` so filtering its table doesn't recompute
   * what's derived from them.
   */
  @observable.ref
  alertModels: AlertModel[] = [];

  /** When the correlation last loaded, unset until it first does. */
  @observable
  fetchedAt?: number;

  /** Cleared here but not yet reported closed: the server can lag behind a close, as the list allows for too. */
  private closePending = false;

  /** Actions taken here that the server's activity log doesn't have yet, with how many of their kind it had then. */
  private pendingActivity: { entry: CorrelationActivityEntry; logged: number }[] = [];

  /**
   * Member alerts still firing, from the loaded alerts once available: the server reports every
   * member of a closed correlation as inactive, whatever the alarm's own state.
   */
  @computed
  get firingAlertCount(): number {
    const alerts = this.alertModels;
    return alerts.length > 0 ? alerts.filter((alert) => !alert.isCleared).length : this.get('activeAlertCount');
  }

  @computed
  get isOpen(): boolean {
    return this.get('status') !== CorrelationStatus.CLOSED;
  }

  @computed
  get isAcked(): boolean {
    return this.get('ackState') === CorrelationAckState.ACKED;
  }

  @computed
  get isMerged(): boolean {
    return this.get('closedReason') === CorrelationClosedReason.MERGED;
  }

  get ackUserId(): string | undefined {
    const ackedByUserId = this.get('ackedByUserId');
    return ackedByUserId ? String(ackedByUserId) : undefined;
  }

  get ackTimestamp(): number | undefined {
    const ackedAt = this.get('ackedAt');
    return ackedAt && timestampMs(ackedAt);
  }

  get statusKey(): CorrelationStatusKey {
    return this.isOpen ? 'open' : 'cleared';
  }

  get ackKey(): CorrelationAckKey {
    return this.isAcked ? 'acked' : 'unacked';
  }

  /** Alert-manager severity key, e.g. `SEVERITY_MAJOR`. */
  get severityKey(): string | undefined {
    return SEVERITY_MAP[Severity[this.get('severity')] as keyof typeof SEVERITY_MAP]?.ALERT_MANAGER;
  }

  get severityLabel(): string {
    return $alerting.getSeverityLabel(this.severityKey);
  }

  get confidenceBand(): ConfidenceBand {
    return CONFIDENCE_BAND_BY_LEVEL[this.get('confidenceLevel')];
  }

  get createdTime(): number | undefined {
    const createdAt = this.get('createdAt');
    return createdAt && timestampMs(createdAt);
  }

  get updatedTime(): number | undefined {
    const updatedAt = this.get('updatedAt');
    return updatedAt && timestampMs(updatedAt);
  }

  get closedTime(): number | undefined {
    const closedAt = this.get('closedAt');
    return closedAt && timestampMs(closedAt);
  }

  /** When the correlation started, i.e. when the engine created it. */
  get startedAt(): number {
    return this.createdTime ?? this.firstAlertAt ?? 0;
  }

  /** When the first member alert fired, usually before the correlation started. Only `Get` carries the members. */
  @computed
  get firstAlertAt(): number | undefined {
    const starts = this.get('members')
      .map((member) => member.startTime)
      .filter((start): start is number => start !== undefined);
    return starts.length > 0 ? Math.min(...starts) : undefined;
  }

  endsAt(now: number = Date.now()): number {
    return this.isOpen ? now : (this.closedTime ?? now);
  }

  durationMs(now: number = Date.now()): number {
    return Math.max(0, this.endsAt(now) - this.startedAt);
  }

  /** Groups member alerts by the rule that raised them. */
  policyKey(alert: AlertModel): string {
    return String(alert.ruleId || alert.policyName || '');
  }

  /** One row per distinct policy across the member alerts. */
  @computed
  get policies(): CorrelationPolicy[] {
    const byRule = new Map<string, CorrelationPolicy>();

    this.alertModels.forEach((alert) => {
      const key = this.policyKey(alert);
      const existing = byRule.get(key);
      if (existing) {
        existing.alertCount += 1;
      } else {
        byRule.set(key, {
          key,
          name: alert.policyName || 'Unnamed policy',
          policyId: alert.policyId ? String(alert.policyId) : undefined,
          application: alert.applicationLabel,
          alertCount: 1
        });
      }
    });

    return [...byRule.values()];
  }

  /** Distinct entities named by the member alerts, keyed on their displayed name. */
  @computed
  get entities(): CorrelationEntity[] {
    const byDisplay = new Map<string, CorrelationEntity>();

    this.alertModels.forEach((alert) => {
      (alert.typedDimensions as TypedAlertDimension[]).forEach(({ key, kind, label, value, display }) => {
        const entityKey = `${kind}::${label}::${display}`;
        const existing = byDisplay.get(entityKey);
        if (existing) {
          existing.alarmIds.push(alert.id);
        } else {
          byDisplay.set(entityKey, { kind, label: label ?? key, value: String(value), display, alarmIds: [alert.id] });
        }
      });
    });

    return [...byDisplay.values()];
  }

  @computed
  get entityCountsByKind(): Record<DimensionKind, number> {
    const counts: Record<DimensionKind, number> = { device: 0, interface: 0, agent: 0, site: 0, other: 0 };
    this.entities.forEach((entity) => {
      counts[entity.kind] += 1;
    });
    return counts;
  }

  /** The member alert furthest from its baseline, as reported by `$alerting.getBaseline`. */
  @computed
  get peakBaseline(): { alert: AlertModel; baseline: AlertBaseline } | null {
    return this.alertModels.reduce<CorrelationModel['peakBaseline']>((peak, alert) => {
      const baseline = $alerting.getBaseline(alert) as AlertBaseline | null;
      if (baseline?.percentage == null) {
        return peak;
      }
      return !peak || baseline.percentage > (peak.baseline.percentage ?? 0) ? { alert, baseline } : peak;
    }, null);
  }

  /** The last load failed because the correlation doesn't exist, rather than for some other reason. */
  get isNotFound(): boolean {
    return Boolean(this.error) && ConnectError.from(this.error?.body).code === Code.NotFound;
  }

  async fetch(): Promise<void> {
    this.setRequestStatus('fetching');
    try {
      const { correlation, events } = await getCorrelation(this.id);
      if (!correlation) {
        throw new ConnectError(`No correlation with id ${this.id}`, Code.NotFound);
      }
      // Unset proto fields (e.g. ackedAt after an un-ack) are absent rather than cleared, so clear them explicitly.
      const next = this.deserialize({ ...correlation, events });
      const dropped = Object.keys(this.attributes).filter((key) => !(key in next));
      this.set({ ...Object.fromEntries(dropped.map((key) => [key, undefined])), ...next });
      if (this.closePending && this.isOpen) {
        this.set({ status: CorrelationStatus.CLOSED });
      } else {
        this.closePending = false;
      }
      this.applyPendingActivity();
      this.markFetched();
    } catch (error) {
      this.error = { label: 'fetching', body: error };
      throw error;
    } finally {
      this.setRequestStatus(null);
    }
  }

  async fetchAlerts(): Promise<void> {
    const alarmIds = this.get('members').map((member) => member.alertId);

    if (alarmIds.length === 0) {
      this.alerts.set([]);
    } else {
      await this.alerts.setServerFilter(
        {
          alarmIds: alarmIds.join(','),
          applications: Object.values(POLICY_APPLICATIONS),
          states: [],
          lookback: undefined,
          // From the first alert, which fired before the correlation started. Start-only: an active range with an
          // end makes alert-manager search alarm phase history, which is far slower.
          active: {
            start: new Date(Math.min(this.firstAlertAt ?? this.startedAt, this.startedAt)).toISOString(),
            end: null
          },
          includeArchived: true,
          // A correlation's alerts include suppressed ones; the default leaves them out.
          suppressed: ALARM_SUPPRESSED_FILTER.INCLUDED
        },
        { force: true },
        { data: { limit: alarmIds.length } }
      );
    }
    this.setAlertModels(this.alerts.unfiltered as unknown as AlertModel[]);
  }

  @action
  setAlertModels(alerts: AlertModel[]): void {
    this.alertModels = alerts;
  }

  @action
  private markFetched(): void {
    this.fetchedAt = Date.now();
    this.error = null;
  }

  /** Actions report as `updating`, so the page can hold off further ones until they finish. */
  private async updating(request: () => Promise<unknown>): Promise<void> {
    this.setRequestStatus('updating');
    try {
      await request();
    } finally {
      this.setRequestStatus(null);
    }
    await this.fetch();
  }

  ack(): Promise<void> {
    return this.updating(async () => {
      await ackCorrelation(this.id);
      this.recordActivity('ack', 'Acknowledged');
    });
  }

  unAck(): Promise<void> {
    return this.updating(async () => {
      await unAckCorrelation(this.id);
      this.recordActivity('unack', 'Unacknowledged');
    });
  }

  close(): Promise<void> {
    return this.updating(async () => {
      await updateCorrelation({ id: this.id, close: true });
      this.closePending = true;
      this.recordActivity('resolution', 'Correlation cleared');
    });
  }

  /** Shows an action in the activity right away, until the server's log has an entry for it. */
  private recordActivity(kind: 'ack' | 'unack' | 'resolution', details: string): void {
    const userId = $auth.activeUser?.id;
    this.pendingActivity.push({
      entry: { timestamp: Date.now(), details, kind, userId: userId ? String(userId) : undefined },
      logged: this.loggedCount(kind)
    });
  }

  private loggedCount(kind: CorrelationActivityEntry['kind']): number {
    const pending = new Set(this.pendingActivity.map(({ entry }) => entry));
    return this.get('activity').filter((entry) => entry.kind === kind && !pending.has(entry)).length;
  }

  private applyPendingActivity(): void {
    this.pendingActivity = this.pendingActivity.filter(({ entry, logged }) => this.loggedCount(entry.kind) <= logged);
    if (this.pendingActivity.length > 0) {
      this.set({ activity: [...this.get('activity'), ...this.pendingActivity.map(({ entry }) => entry)] });
    }
  }

  async removeAlerts(alertIds: string[]): Promise<void> {
    await this.updating(() => removeAlertsFromCorrelation({ id: this.id, alertIds }));
    await this.fetchAlerts();
  }

  deserialize({
    $typeName: _typeName,
    $unknown: _unknown,
    alerts,
    events,
    ...correlation
  }: CorrelationInput): CorrelationAttributes {
    return {
      ...correlation,
      members: alerts.map((alert) => ({
        alertId: alert.alertId,
        active: alert.active,
        startTime: alert.alarmStartTime && timestampMs(alert.alarmStartTime),
        addedAt: alert.addedAt && timestampMs(alert.addedAt),
        addedByUserId: alert.addedByUserId ? String(alert.addedByUserId) : undefined
      })),
      activity: (events ?? [])
        .map((event) => this.deserializeActivity(event))
        .filter((entry): entry is CorrelationActivityEntry => entry !== null)
    };
  }

  private deserializeActivity(event: ContextInput): CorrelationActivityEntry | null {
    const timestamp = event.eventTime ? timestampMs(event.eventTime) : 0;
    const { userId, userName } = event.producer?.apiRequest ?? {};
    const user = { userId: userId || undefined, userName: userName || undefined };

    switch (event.input.case) {
      case 'creation':
        return {
          timestamp,
          details: event.input.value.title || 'Correlation created',
          kind: 'creation',
          // The alerts the engine grouped to open the correlation, before any joined later.
          addedAlerts: event.input.value.alertIds.length,
          alertIds: event.input.value.alertIds
        };
      case 'update': {
        const { update } = event.input.value;
        if (update.case === 'engineUpdate') {
          return {
            timestamp,
            details: update.value.summary || 'Correlation updated by the engine',
            kind: 'engine',
            confidence: update.value.confidence,
            addedAlerts: update.value.addedAlertIds.length,
            alertIds: update.value.addedAlertIds
          };
        }
        if (update.case === 'manualUpdate') {
          return {
            timestamp,
            details: update.value.description || 'Correlation updated manually',
            kind: 'manual',
            addedAlerts: update.value.addedAlertIds.length,
            alertIds: update.value.addedAlertIds,
            removedAlerts: update.value.removedAlertIds.length,
            ...user
          };
        }
        return { timestamp, details: 'Correlation updated' };
      }
      case 'resolution':
        return {
          timestamp,
          details: event.input.value.description || 'Correlation cleared',
          kind: 'resolution',
          merged: event.input.value.closeReason === CorrelationCloseReason.MERGED,
          ...user
        };
      case 'acknowledgement':
        return { timestamp, details: event.input.value.description || 'Acknowledged', kind: 'ack', ...user };
      case 'unacknowledgement':
        return { timestamp, details: event.input.value.description || 'Unacknowledged', kind: 'unack', ...user };
      default:
        return null;
    }
  }
}
